import { type FormEvent, useEffect, useState } from 'react'
import { toDataURL } from 'qrcode'

import { useCurrentUser, useTotpConfirm, useTotpDisable, useTotpSetup } from '../api/auth'
import { useWithStepUp } from '../lib/useWithStepUp'
import { Modal } from './Modal'
import { PasswordField } from './PasswordField'
import { StepUpDialog } from './StepUpDialog'
import { useToast } from './Toast'

type SetupData = { secret: string; provisioning_uri: string }

/** Activar: setup → confirmar código → mostrar códigos de recuperación (una
 *  sola vez). Desactivar: sólo pide la contraseña. Las dos ramas comparten
 *  el mismo useWithStepUp — RequireStepUp cubre las tres llamadas al
 *  backend (setup/confirm/disable), y un step-up ya vigente (∼5 min) no
 *  vuelve a pedir contraseña entre setup y confirmar.
 *
 *  Contenido sin el `<Modal>` propio — así `ProfileDialog` lo puede meter
 *  en una de sus pestañas sin anidar dos modales. `TwoFactorDialog` de acá
 *  abajo es el mismo contenido para cuando se usa suelto. */
export function TwoFactorForm({ onClose }: { onClose: () => void }) {
  const { data: user } = useCurrentUser()
  const totpSetup = useTotpSetup()
  const totpConfirm = useTotpConfirm()
  const totpDisable = useTotpDisable()
  const stepUp = useWithStepUp()
  const toast = useToast()

  const [setupData, setSetupData] = useState<SetupData | null>(null)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [savedCodes, setSavedCodes] = useState(false)
  const [disablePassword, setDisablePassword] = useState('')

  useEffect(() => {
    if (!setupData) {
      setQrDataUrl(null)
      return
    }
    let cancelled = false
    toDataURL(setupData.provisioning_uri).then((url) => {
      if (!cancelled) setQrDataUrl(url)
    })
    return () => {
      cancelled = true
    }
  }, [setupData])

  async function handleStart() {
    await stepUp.run(async () => {
      setSetupData(await totpSetup.mutateAsync())
    })
  }

  async function handleConfirm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    await stepUp.run(async () => {
      const result = await totpConfirm.mutateAsync(code)
      setRecoveryCodes(result.codes)
    })
  }

  async function handleDisable(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    await stepUp.run(async () => {
      const result = await totpDisable.mutateAsync(disablePassword)
      toast.success(result.message)
      onClose()
    })
  }

  async function copySecret() {
    await navigator.clipboard.writeText(setupData?.secret ?? '')
    toast.info('Secreto copiado')
  }

  async function copyRecoveryCodes() {
    await navigator.clipboard.writeText((recoveryCodes ?? []).join('\n'))
    toast.info('Códigos copiados')
  }

  return (
    <>
      {recoveryCodes ? (
          <>
            <h3>Guardá tus códigos de recuperación</h3>
            <p className="muted">
              Si perdés el dispositivo de la app autenticadora, cada uno sirve una sola vez para
              entrar. No se van a volver a mostrar.
            </p>
            <div className="totp-recovery-codes mono">
              {recoveryCodes.map((recoveryCode) => (
                <span key={recoveryCode}>{recoveryCode}</span>
              ))}
            </div>
            <button type="button" className="button button--ghost button--small" onClick={copyRecoveryCodes}>
              Copiar todos
            </button>
            <label className="field field--checkbox">
              <input
                type="checkbox"
                checked={savedCodes}
                onChange={(event) => setSavedCodes(event.target.checked)}
              />
              Ya los guardé en un lugar seguro
            </label>
            <div className="confirm-dialog__actions">
              <button className="button" type="button" disabled={!savedCodes} onClick={onClose}>
                Listo
              </button>
            </div>
          </>
        ) : setupData ? (
          <form onSubmit={handleConfirm}>
            <h3>Escaneá el código</h3>
            <p className="muted">
              Con la app autenticadora (Google Authenticator, 1Password, Authy) — o cargá el
              secreto a mano si no podés escanear.
            </p>
            {qrDataUrl && (
              <img src={qrDataUrl} alt="Código QR para la app autenticadora" className="totp-qr" />
            )}
            <div className="totp-secret">
              <code className="mono">{setupData.secret}</code>
              <button type="button" className="button button--ghost button--small" onClick={copySecret}>
                Copiar
              </button>
            </div>

            {totpConfirm.isError && !stepUp.promptOpen && (
              <div className="alert" role="alert">
                {totpConfirm.error.message}
              </div>
            )}

            <div className="field">
              <label htmlFor="totp-code">Código de 6 dígitos</label>
              <input
                id="totp-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                minLength={6}
                maxLength={8}
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </div>

            <div className="confirm-dialog__actions">
              <button className="button button--ghost" type="button" onClick={onClose}>
                Cancelar
              </button>
              <button className="button" type="submit" disabled={totpConfirm.isPending}>
                {totpConfirm.isPending ? 'Confirmando…' : 'Confirmar'}
              </button>
            </div>
          </form>
        ) : user?.totp_enabled ? (
          <form onSubmit={handleDisable}>
            <h3>Desactivar verificación en dos pasos</h3>
            <p className="muted">
              Vas a poder entrar de nuevo con sólo la contraseña. Se borran también los códigos de
              recuperación que te quedaban.
            </p>

            {totpDisable.isError && !stepUp.promptOpen && (
              <div className="alert" role="alert">
                {totpDisable.error.message}
              </div>
            )}

            <PasswordField
              id="totp-disable-password"
              label="Contraseña"
              value={disablePassword}
              onChange={setDisablePassword}
              autoComplete="current-password"
              autoFocus
              required
            />

            <div className="confirm-dialog__actions">
              <button className="button button--ghost" type="button" onClick={onClose}>
                Cancelar
              </button>
              <button className="button button--danger" type="submit" disabled={totpDisable.isPending}>
                {totpDisable.isPending ? 'Desactivando…' : 'Desactivar'}
              </button>
            </div>
          </form>
        ) : (
          <>
            <h3>Verificación en dos pasos</h3>
            <p className="muted">
              Sumá un código de una app autenticadora al iniciar sesión, además de la contraseña.
            </p>

            {totpSetup.isError && !stepUp.promptOpen && (
              <div className="alert" role="alert">
                {totpSetup.error.message}
              </div>
            )}

            <div className="confirm-dialog__actions">
              <button className="button button--ghost" type="button" onClick={onClose}>
                Cancelar
              </button>
              <button className="button" type="button" disabled={totpSetup.isPending} onClick={handleStart}>
                {totpSetup.isPending ? 'Generando…' : 'Activar'}
              </button>
            </div>
          </>
        )}

      {stepUp.promptOpen && (
        <StepUpDialog
          onCancel={stepUp.cancel}
          onConfirm={stepUp.confirm}
          isPending={stepUp.isConfirming}
          error={stepUp.confirmError}
          reason="La verificación en dos pasos es una acción sensible — hace falta confirmar la contraseña de nuevo."
        />
      )}
    </>
  )
}

export function TwoFactorDialog({ onClose }: { onClose: () => void }) {
  return (
    <Modal onClose={onClose}>
      <TwoFactorForm onClose={onClose} />
    </Modal>
  )
}
